import React from 'react'

import * as c from '../../../common'
import {
  isSigningFlowV2,
  signingFlowStamp,
} from '../../../common/helpers/signingFlowV2'

import * as r from '../../../react-utils'

import Button from '@mui/material/Button'

import Pad from '../../comps/Pad'
import { auth } from '../../fire'
import { useGuidedHeader } from '../../helpers/guidedSigningHeader'

//@ts-ignore
import styles from './Signature.module.css'

export interface CustomerSignatureProps {
  customerID: string
  field: keyof c.Customer
  onNewSignatureSaved?: (field: keyof c.Customer, value: string) => void
  onRequestClose(): void
  /**
   * Extra fields written in the SAME update as the signature, read when Save
   * is pressed (e.g. the amount a payment signature freezes, V67).
   */
  extraOnSave?: () => c.PartialCustomer
}

export default React.memo<CustomerSignatureProps>(function Signature({
  customerID,
  field,
  onNewSignatureSaved,
  onRequestClose,
  extraOnSave,
}) {
  const selectCustomerField = React.useMemo(
    (): ReturnType<typeof c.makeSelectCustomerField> =>
      c.makeSelectCustomerField(),
    [],
  )
  const selectCustomerFieldArgs = React.useMemo(
    (): c.SelectCustomerFieldParams => ({
      customerID,
      field,
    }),
    [customerID, field],
  )

  // Remember to subscribe to the customer elsewhere!
  const currentSignature = c.useSelector(
    (_): c.ValueOf<c.Customer> =>
      selectCustomerField(_, selectCustomerFieldArgs),
  )

  // Signing v2: a still-unsigned customer is marked 'v2' with their first
  // signature, so the layout they signed on never flips back (V67).
  const customerRecord = c.useSelector(
    (_) => _.customers.data[customerID] as Record<string, unknown> | undefined,
  )

  const signature = React.useRef<HTMLCanvasElement>(
    document.createElement('canvas'),
  )

  const isMounted = r.useIsMounted()

  // Guided signing (13197251229): say what this signature means while signing.
  const guided = useGuidedHeader(field as string)

  const draw = React.useCallback((posX: number, posY: number) => {
    const context = signature.current.getContext('2d')
    if (!context) return

    context.beginPath()
    context.moveTo(initialX as number, initialY as number)
    context.lineWidth = 2
    context.strokeStyle = '#000'
    context.lineCap = 'round'
    context.lineJoin = 'round'
    context.lineTo(posX, posY)
    context.stroke()
    initialX = posX
    initialY = posY
  }, [])

  const getRelativePosition = (clientX: number, clientY: number) => {
    const rect = signature.current.getBoundingClientRect()
    const scaleX = signature.current.width / rect.width
    const scaleY = signature.current.height / rect.height

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    }
  }

  const touchMove = React.useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      e.preventDefault()
      const touches = e.changedTouches

      if (touches[0]) {
        const { x, y } = getRelativePosition(
          touches[0]?.clientX,
          touches[0]?.clientY,
        )
        draw(x, y)
      }
    },
    [draw],
  )

  const mouseMove = React.useCallback(
    (e: { clientX: number; clientY: number }) => {
      const { x, y } = getRelativePosition(e.clientX, e.clientY)
      draw(x, y)
    },
    [draw],
  )

  const mouseUp = React.useCallback(() => {
    signature.current.removeEventListener('mousemove', mouseMove)
    initialX = undefined
    initialY = undefined
  }, [mouseMove])

  const mouseDown = React.useCallback(
    (e: { clientX: number; clientY: number }) => {
      const { x, y } = getRelativePosition(e.clientX, e.clientY)
      initialX = x
      initialY = y
      draw(x, y)
      signature.current.addEventListener('mousemove', mouseMove)
    },
    [draw, mouseMove],
  )

  const touchStart = React.useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      const touches = e.changedTouches

      if (touches[0]) {
        const { x, y } = getRelativePosition(
          touches[0]?.clientX,
          touches[0]?.clientY,
        )
        initialX = x
        initialY = y
        draw(x, y)
        signature.current.addEventListener(
          'touchmove',
          //@ts-expect-error TO-DO
          touchMove,
        )
      }
    },
    [draw, touchMove],
  )

  const touchUp = React.useCallback(() => {
    signature.current.removeEventListener(
      'touchmove',
      //@ts-expect-error TO-DO
      touchMove,
    )
    initialX = undefined
    initialY = undefined
  }, [touchMove])

  const isCanvasEmpty = React.useCallback(() => {
    const canvas = signature.current
    const context = signature.current.getContext('2d')

    if (!context) return

    const pixelBuffer = new Uint32Array(
      context.getImageData(0, 0, canvas.width, canvas.height).data.buffer,
    )

    return !pixelBuffer.some((color) => color !== 0)
  }, [])

  const compressSignature = (): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const signatureCanvas = signature.current
      signatureCanvas.toBlob(
        (blob) => {
          if (blob === null) {
            reject(blob)
          } else {
            resolve(blob)
          }
        },
        'image/jpg',
        0.1,
      )
    })
  }

  const signatureToBase64 = React.useCallback(async () => {
    if (isCanvasEmpty()) {
      alert('Signature is empty')
      return
    }

    if (
      currentSignature &&
      !window.confirm(
        'Replace the existing signature? The previous one will be backed up.',
      )
    ) {
      return
    }

    const image = await compressSignature()

    if (!isMounted()) {
      return
    }
    let reader = new FileReader()
    reader.readAsDataURL(image)
    reader.onload = () => {
      if (!isMounted()) {
        return
      }
      const base64String = reader.result

      const currentDate = c.normalizeTimestampToMs(Date.now())

      const signDateKey = `${field}_date`
      c.updateCustomer(customerID, {
        ...signingFlowStamp(customerRecord),
        ...(extraOnSave ? extraOnSave() : {}),
        ...(currentSignature
          ? { c_signature_backup: currentSignature as string }
          : {}),
        [field]: base64String as string,
        [signDateKey]: currentDate,
      })

      const isNewSignature = !currentSignature
      onRequestClose()
      // Replacements must not resend; only primary contract-signature modals opt in.
      if (isNewSignature) {
        onNewSignatureSaved?.(field, base64String as string)
      }
    }
  }, [
    currentSignature,
    customerRecord,
    extraOnSave,
    isCanvasEmpty,
    isMounted,
    field,
    customerID,
    onRequestClose,
    onNewSignatureSaved,
  ])

  const isAuthorized = authorizedEmails.includes(
    auth.currentUser?.email as string,
  )

  const clearCanvas = React.useCallback(() => {
    signature.current
      .getContext('2d')!
      .clearRect(0, 0, signature.current.width, signature.current.height)
  }, [])

  const deleteSavedSignature = React.useCallback(() => {
    if (
      !window.confirm(
        'Delete the saved signature? A backup will be kept in c_signature_backup.',
      )
    )
      return

    const signDateKey = `${field}_date`
    c.updateCustomer(customerID, {
      ...(currentSignature
        ? { c_signature_backup: currentSignature as string }
        : {}),
      [field]: '',
      [signDateKey]: '',
    })
    console.warn(
      `[SIGNATURE DELETED] field=${field} customer=${customerID} by=${auth.currentUser?.email}`,
    )
  }, [customerID, field, currentSignature])

  React.useEffect((): c.VoidFn => {
    if (!customerID) {
      return c.EMPTY_FN
    }
    c.dispatch(
      c.subToSingleCustomer({
        customerID: customerID,
      }),
    )
    return () => {
      c.dispatch(
        c.unSubFromSingleCustomer({
          customerID,
        }),
      )
    }
  }, [customerID])

  // Signing v2 phase C (ticket 13213634281): DocuSign-style dialog. Same
  // canvas, same handlers, same writes; only the look changes, and only for
  // v2 customers (everyone who signed before keeps the dialog they saw, V67).
  const v2 = c.IS_MAIN && isSigningFlowV2(customerRecord)
  const initials = /initials/.test(field as string)
  if (v2)
    return (
      <div style={dsx.dialog} aria-label="Signature pad">
        <div style={dsx.header}>
          <p style={dsx.eyebrow}>
            {guided
              ? (guided.part ? guided.part + ' · ' : '') +
                'Step ' +
                guided.index +
                ' of ' +
                guided.total
              : initials
              ? 'Initials'
              : 'Signature'}
          </p>
          <p style={dsx.title}>
            {guided
              ? guided.title
              : initials
              ? 'Adopt your initials'
              : 'Adopt your signature'}
          </p>
          {guided && <p style={dsx.text}>{guided.description}</p>}
          {guided?.signAllLabels && guided.signAllLabels.length > 1 && (
            <p style={guidedSx.notice}>
              Signing all at once: this signature will also be placed on{' '}
              {guided.signAllLabels.slice(1).join(', ')}.
            </p>
          )}
        </div>
        <p style={dsx.hint}>
          {initials
            ? 'Draw your initials in the box below.'
            : 'Draw your signature in the box below.'}
        </p>
        <div style={dsx.pad}>
          <canvas
            onTouchStart={touchStart}
            onTouchEnd={touchUp}
            onMouseDown={mouseDown}
            onMouseUp={mouseUp}
            height={320}
            ref={signature}
            style={dsx.canvas}
            width={480}
          />
          <span aria-hidden style={dsx.x}>
            x
          </span>
          <span aria-hidden style={dsx.baseline} />
        </div>
        <div style={dsx.actions}>
          {guided && (
            <Button
              variant="text"
              onClick={() => {
                onRequestClose()
                guided.viewContract()
              }}
            >
              View agreement
            </Button>
          )}
          {isAuthorized && (
            <Button color="error" variant="text" onClick={deleteSavedSignature}>
              Delete saved
            </Button>
          )}
          <span style={{ flex: 1 }} />
          <Button variant="outlined" onClick={clearCanvas} sx={dsx.secondary}>
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={signatureToBase64}
            sx={dsx.primary}
          >
            Adopt and sign
          </Button>
        </div>
      </div>
    )

  return (
    <>
      {/* Room for the Modal's close button above the legacy layout. */}
      <div style={{ height: 28 }} />
      {guided && (
        <div style={guidedSx.card} aria-label="What you are signing">
          <p style={guidedSx.eyebrow}>
            Step {guided.index} of {guided.total}
          </p>
          <p style={guidedSx.title}>{guided.title}</p>
          <p style={guidedSx.text}>{guided.description}</p>
          {guided.signAllLabels && guided.signAllLabels.length > 1 && (
            <p style={guidedSx.notice}>
              Signing all at once: this signature will also be placed on{' '}
              {guided.signAllLabels.slice(1).join(', ')}.
            </p>
          )}
          <Button
            size="small"
            variant="outlined"
            sx={{ marginTop: 1 }}
            onClick={() => {
              onRequestClose()
              guided.viewContract()
            }}
          >
            View agreement
          </Button>
        </div>
      )}
      <canvas
        onTouchStart={touchStart}
        onTouchEnd={touchUp}
        onMouseDown={mouseDown}
        onMouseUp={mouseUp}
        height={320}
        ref={signature}
        className={styles['canvas']}
        width={480}
      />

      <Pad amt={20} />
      <div style={sx.containerButtons}>
        <Button variant="contained" onClick={signatureToBase64}>
          Save
        </Button>

        <Button variant="contained" color="warning" onClick={clearCanvas}>
          Clear drawing
        </Button>

        {isAuthorized && (
          <Button
            variant="contained"
            color="error"
            onClick={deleteSavedSignature}
          >
            Delete saved
          </Button>
        )}
      </div>
    </>
  )
})

const guidedSx = {
  card: {
    background: '#fff',
    borderLeft: '4px solid #ed6c02',
    borderRadius: 10,
    boxSizing: 'border-box',
    marginBottom: 12,
    maxWidth: 480,
    padding: '12px 16px',
    width: '100%',
  } as React.CSSProperties,
  eyebrow: {
    color: '#5f6b75',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.6,
    margin: 0,
    textTransform: 'uppercase',
  } as React.CSSProperties,
  title: { fontSize: 18, fontWeight: 700, margin: '2px 0' },
  text: { color: '#37474f', fontSize: 14, margin: 0 },
  notice: {
    background: '#fff4e5',
    borderRadius: 6,
    fontSize: 13,
    margin: '8px 0 0',
    padding: '6px 8px',
  },
}

// DocuSign-style (v2): white pad on a baseline with an "x", brand blue
// primary action. Baseline and "x" are overlays, never drawn into the image.
const BRAND = '#1976d2'
const dsx = {
  // The Modal card supplies background, radius, 20px padding and the close
  // button (top-right); the dialog just fills it (Jaron, Oct 30).
  dialog: {
    boxSizing: 'border-box',
    maxWidth: '100%',
    width: 480,
  } as React.CSSProperties,
  header: {
    borderBottom: '1px solid #e3e7ea',
    marginBottom: 12,
    paddingBottom: 10,
    // Clear of the Modal's 36px close button.
    paddingRight: 36,
  } as React.CSSProperties,
  eyebrow: {
    color: '#5f6b75',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.6,
    margin: 0,
    textTransform: 'uppercase',
  } as React.CSSProperties,
  title: { color: '#1c2b36', fontSize: 20, fontWeight: 700, margin: '2px 0' },
  text: { color: '#37474f', fontSize: 14, margin: 0 },
  hint: { color: '#5f6b75', fontSize: 13, margin: '0 0 6px' },
  pad: {
    background: '#fff',
    border: '1px solid #c9d1d8',
    borderRadius: 6,
    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)',
    position: 'relative',
  } as React.CSSProperties,
  canvas: {
    background: 'transparent',
    cursor: 'crosshair',
    display: 'block',
    height: 'auto',
    // iPad / iOS WebView: drawing must never scroll or zoom the page.
    touchAction: 'none',
    width: '100%',
  } as React.CSSProperties,
  x: {
    bottom: '26%',
    color: '#5f6b75',
    fontSize: 22,
    fontWeight: 700,
    left: '6%',
    lineHeight: 1,
    pointerEvents: 'none',
    position: 'absolute',
  } as React.CSSProperties,
  baseline: {
    borderBottom: '2px solid #9aa5b1',
    bottom: '24%',
    left: '6%',
    pointerEvents: 'none',
    position: 'absolute',
    right: '6%',
  } as React.CSSProperties,
  actions: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 14,
  } as React.CSSProperties,
  primary: {
    backgroundColor: BRAND,
    fontWeight: 700,
    textTransform: 'none',
  },
  secondary: { borderColor: BRAND, color: BRAND, textTransform: 'none' },
}

const authorizedEmails = [
  'danlugo92@gmail.com',
  'castellanorick@gmail.com',
  'ricardo@unitedpowersolutions.com',
  'erwinjv98@gmail.com',
]

let initialX: unknown
let initialY: unknown

const sx = {
  containerButtons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
} as const
